import { APP_API_BASE, APP_API_V1_BASE } from "../../../config/api";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  Stack,
  Card,
  CardContent,
  Divider,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  MenuItem,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AnalyticsModel from "./AnalyticsModel";
import axios from "axios";
import DeviceCredentialsCard from "../setting/DeviceCredentialsCard";
import DeviceSettings from "../setting/DeviceSettings";
import DeviceCommandsCard from "../setting/DeviceCommandsCard.js";
import { Download, Refresh } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import AuditLogsTable from "../telemtry/AuditLogsTable";
import DeviceLogsTable from "../telemtry/DeviceLogsTable";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { resolveDeviceLicenseStatus } from "../Devices";
import { resolveDeviceStatus } from "../deviceStatus";
import RemoteAccess from "../RemoteAccess";
import FirmwareOverview from "../FirmwareTab/FirmwareOverview";
import FirmwareStats from "../FirmwareTab/FirmwareCards";
import FirmwareUpdateHistory from "../FirmwareTab/FirmwareUpdateHistory";
import FirmwareDiagnosticsPanel from "../FirmwareTab/FirmwareDiagnosticsPanel";
import CurlMqttCard from "../setting/CurlMqttCard";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import RelayControls from "../RelayControl";
import DeviceAlarms from "../DeviceAlarm";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  ResponsiveContainer,
  LineChart,
  AreaChart,
  BarChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_BASE = `${APP_API_BASE}`;
const INITIAL_TELEMETRY_LIMIT = 60;
const DELTA_TELEMETRY_LIMIT = 100;
const MAX_TELEMETRY_ROWS_IN_MEMORY = 1000;

const DASHBOARD_PALETTE = [
  "#2563EB",
  "#0F766E",
  "#7C3AED",
  "#EA580C",
  "#DC2626",
  "#059669",
];

const panelSx = {
  borderRadius: 5,
  border: "1px solid rgba(148, 163, 184, 0.18)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
};

const gridSurfaceSx = {
  border: "none",
  backgroundColor: "#FFF",
  "& .MuiDataGrid-columnHeaders": {
    background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
    borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
    color: "#334155",
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
    outline: "none !important",
  },
  "& .MuiDataGrid-row": {
    borderBottom: "1px solid rgba(241, 245, 249, 0.9)",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#F8FAFC",
  },
  "& .MuiDataGrid-cell": {
    color: "#0F172A",
    py: 1.2,
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    backgroundColor: "#FCFDFE",
  },
};

const normalizeFieldType = (type) => {
  if (!type) return "Unknown";
  const value = String(type).toLowerCase();

  if (value === "number" || value === "float") return "Float";
  if (value === "integer" || value === "int") return "Integer";
  if (value === "boolean" || value === "bool") return "Boolean";

  return String(type);
};

const resolveUnit = (field) => {
  if (!field) return "";
  if (field.unit === "Custom") return field.customUnit || "";
  return field.unit || "";
};

const toDisplayValue = (value, unit = "") => {
  if (value === null || value === undefined || value === "") return "--";
  return `${value}${unit ? ` ${unit}` : ""}`;
};

const evaluateThreshold = (rules, metricKey, rawValue) => {
  const numericValue = Number(rawValue);
  if (!Array.isArray(rules) || Number.isNaN(numericValue)) {
    return { label: "Normal", tone: "#166534", bg: "#ECFDF3" };
  }

  const rule = rules.find((item) => item.metric_key === metricKey);
  if (!rule) {
    return { label: "Normal", tone: "#166534", bg: "#ECFDF3" };
  }

  const warning = Number(rule.warning_threshold);
  const critical = Number(rule.critical_threshold);
  const operator = rule.operator || ">";

  const compare = (threshold) => {
    if (Number.isNaN(threshold)) return false;
    if (operator === ">") return numericValue > threshold;
    if (operator === ">=") return numericValue >= threshold;
    if (operator === "<") return numericValue < threshold;
    if (operator === "<=") return numericValue <= threshold;
    return false;
  };

  if (compare(critical)) {
    return { label: "Critical", tone: "#B42318", bg: "#FEF3F2" };
  }
  if (compare(warning)) {
    return { label: "Warning", tone: "#B54708", bg: "#FFFAEB" };
  }
  return { label: "Normal", tone: "#166534", bg: "#ECFDF3" };
};

const getWidgetColor = (index) =>
  DASHBOARD_PALETTE[index % DASHBOARD_PALETTE.length];

const getGaugePercent = (widget, numericValue, thresholdRules) => {
  if (Number.isNaN(numericValue)) return 0;
  const rule = Array.isArray(thresholdRules)
    ? thresholdRules.find((item) => item.metric_key === widget.metricKey)
    : null;

  const maxThreshold =
    Number(rule?.critical_threshold) || Number(rule?.warning_threshold) || 100;

  if (!maxThreshold || Number.isNaN(maxThreshold)) {
    return Math.max(0, Math.min(100, numericValue));
  }

  return Math.max(0, Math.min(100, (numericValue / maxThreshold) * 100));
};

const packetTypeTone = (packetType) => {
  const value = String(packetType || "telemetry").toLowerCase();
  switch (value) {
    case "heartbeat":
      return { bg: "#EFF6FF", color: "#1D4ED8" };
    case "event":
      return { bg: "#FEF3C7", color: "#B45309" };
    case "diagnostics":
      return { bg: "#F3E8FF", color: "#7E22CE" };
    case "state_snapshot":
      return { bg: "#DCFCE7", color: "#166534" };
    default:
      return { bg: "#E0F2FE", color: "#0369A1" };
  }
};

const formatTelemetryCellValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (err) {
      return String(value);
    }
  }
  return String(value);
};

const formatTelemetryCount = (value) =>
  Number(value || 0).toLocaleString("en-IN");

const hashTelemetryValue = (value) => {
  let text = "";
  try {
    text = JSON.stringify(value ?? {});
  } catch (err) {
    text = String(value ?? "");
  }

  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }

  return Math.abs(hash).toString(36);
};

const getTelemetryPayload = (row) => {
  if (
    row?.__raw_payload &&
    typeof row.__raw_payload === "object" &&
    !Array.isArray(row.__raw_payload)
  ) {
    return row.__raw_payload;
  }

  if (row?.data && typeof row.data === "object" && !Array.isArray(row.data)) {
    return row.data;
  }

  const {
    id,
    telemetry_id,
    _id,
    timestamp,
    ts,
    server_time,
    created_at,
    packet_type,
    type,
    data,
    ...payload
  } = row || {};

  return payload;
};

const flattenTelemetryPayload = (payload, prefix = "", target = {}) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return target;
  }

  Object.entries(payload).forEach(([key, value]) => {
    if (!key || String(key).startsWith("__")) return;

    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenTelemetryPayload(value, path, target);
      return;
    }

    target[path] = value;
  });

  return target;
};

const buildTelemetryRowId = (row, index = 0) => {
  const existingId = row?.id || row?.telemetry_id || row?._id;
  if (existingId) return String(existingId);

  const payload = getTelemetryPayload(row);
  const timestamp =
    row?.timestamp || row?.ts || payload?.timestamp || payload?.ts || "no-ts";
  const serverTime =
    row?.server_time ||
    row?.created_at ||
    payload?.server_time ||
    "no-server-ts";
  const packetType =
    row?.packet_type ||
    row?.type ||
    payload?.packet_type ||
    payload?.type ||
    "telemetry";

  return `${timestamp}-${serverTime}-${packetType}-${hashTelemetryValue(payload)}-${index}`;
};

const normalizeTelemetryRow = (row, index = 0) => {
  const payload = getTelemetryPayload(row);
  const flattenedPayload = flattenTelemetryPayload(payload);

  return {
    ...payload,
    ...flattenedPayload,
    __raw_payload: payload,
    timestamp: row?.timestamp || row?.ts || payload?.timestamp || null,
    server_time:
      row?.server_time || row?.created_at || payload?.server_time || null,
    packet_type:
      row?.packet_type || row?.type || payload?.packet_type || "telemetry",
    id: buildTelemetryRowId(row, index),
  };
};

const mergeSchemaDefinitions = (primary = [], fallback = []) => {
  const merged = [];
  const seen = new Set();

  [...primary, ...fallback].forEach((field) => {
    if (!field?.key || seen.has(field.key)) return;
    seen.add(field.key);
    merged.push(field);
  });

  return merged;
};

export default function Model() {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const { state } = useLocation();
  const { permissions, activeOrg } = useAuth();

  const [latestData, setLatestData] = useState([]);
  const [telemetryTotalCount, setTelemetryTotalCount] = useState(null);
  const [telemetryCountLoading, setTelemetryCountLoading] = useState(false);
  const [telemetryCountError, setTelemetryCountError] = useState("");
  const [telemetryHasMore, setTelemetryHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [olderTelemetryLoading, setOlderTelemetryLoading] = useState(false);
  const [deviceDetail, setDeviceDetail] = useState(state?.device || null);
  const [deviceTypeDetails, setDeviceTypeDetails] = useState(null);
  const [deviceMetaLoading, setDeviceMetaLoading] = useState(false);
  const [firmwareHistory, setFirmwareHistory] = useState([]);
  const [firmwareLoading, setFirmwareLoading] = useState(false);
  const [licenseHistory, setLicenseHistory] = useState([]);
  const [telemetryPagination, setTelemetryPagination] = useState({
    page: 0,
    pageSize: 20,
  });
  const [packetTypeFilter, setPacketTypeFilter] = useState("all");
  const [rawJsonDialogOpen, setRawJsonDialogOpen] = useState(false);
  const [rawJsonCopyStatus, setRawJsonCopyStatus] = useState("");
  const [licenseActionOpen, setLicenseActionOpen] = useState(false);
  const [licenseActionType, setLicenseActionType] = useState("extend");
  const [licenseActionLoading, setLicenseActionLoading] = useState(false);
  const [licenseActionError, setLicenseActionError] = useState("");
  const [licenseActionSuccess, setLicenseActionSuccess] = useState("");
  const [licenseActionForm, setLicenseActionForm] = useState({
    durationValue: 1,
    durationUnit: "year",
    reason: "",
    releaseEntitlement: false,
  });
  const lastTimestampRef = useRef(null);
  const telemetryKeysRef = useRef(new Set());

  useEffect(() => {
    if (state?.device) {
      setDeviceDetail(state.device);
      return;
    }

    if (!deviceId) return;

    fetchDeviceDetails();
  }, [state?.device, deviceId, activeOrg?.id]);

  useEffect(() => {
    const resolvedDevice = state?.device || deviceDetail;
    const typeId =
      resolvedDevice?.device_type?.id || resolvedDevice?.device_type_id || null;

    if (!typeId) {
      setDeviceTypeDetails(null);
      return;
    }

    const fetchDeviceType = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const res = await axios.get(
          `${API_BASE}/api/v1/device-types/${typeId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
            },
          },
        );

        setDeviceTypeDetails(res.data || null);
      } catch (error) {
        console.error("Failed to fetch device type configuration", error);
        setDeviceTypeDetails(null);
      }
    };

    fetchDeviceType();
  }, [state?.device, deviceDetail, activeOrg?.id]);

  const device = state?.device || deviceDetail;
  const deviceStatus = device ? resolveDeviceStatus(device) : null;
  const licenseStatus = device ? resolveDeviceLicenseStatus(device) : null;

  const isOnline = Boolean(device?.is_active);

  const [activeTab, setActiveTab] = React.useState(0);
  const [settingTab, setSettingTab] = useState(0);
  const inheritedSchema = deviceTypeDetails?.config?.schema_definition || [];
  const deviceSchema = Array.isArray(device?.schema) ? device.schema : [];
  const schema = useMemo(
    () => mergeSchemaDefinitions(inheritedSchema, deviceSchema),
    [inheritedSchema, deviceSchema],
  );

  const fetchDeviceDetails = async () => {
    if (!deviceId) return;
    try {
      setDeviceMetaLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const res = await axios.get(`${API_BASE}/api/v1/devices/${deviceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      });

      setDeviceDetail(res.data || null);
    } catch (error) {
      console.error("Failed to fetch device details", error);
    } finally {
      setDeviceMetaLoading(false);
    }
  };

  // useEffect(() => {
  //   if (!state.device?.id) return;

  //   let isMounted = true; // guard against state updates after unmount

  //   const fetchTelemetry = async () => {
  //     try {
  //       const token = localStorage.getItem("authToken");
  //       if (!token) return;

  //       setLoading(true);

  //       const res = await axios.get(
  //         `${APP_API_V1_BASE}/devices/${state.device.id}/telemetry`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );

  //       if (!isMounted) return;

  //       // ðŸ”„ normalize response
  //       const normalized = (res.data?.data || []).map((row) => ({
  //         timestamp: row.timestamp,
  //         ...row.data,
  //       }));

  //       setLatestData(normalized);
  //     } catch (err) {
  //       if (isMounted) {
  //         console.error("Failed to fetch telemetry", err);
  //         setLatestData([]);
  //       }
  //     } finally {
  //       if (isMounted) {
  //         setLoading(false);
  //       }
  //     }
  //   };

  //   // â±ï¸ initial fetch immediately
  //   fetchTelemetry();

  //   // ðŸ” poll every 5 seconds
  //   const intervalId = setInterval(fetchTelemetry, 5000);

  //   // ðŸ§¹ cleanup on device change or unmount
  //   return () => {
  //     isMounted = false;
  //     clearInterval(intervalId);
  //   };
  // }, [state.device?.id]);

  const handleDownloadCSV = () => {
    if (!latestData || latestData.length === 0 || !device) return;

    // CSV headers
    const headers = [
      "timestamp",
      "server_time",
      "packet_type",
      ...schema.map((item) => item.key),
    ];

    // CSV rows
    const rows = latestData.map((row) =>
      headers.map((header) => row[header] ?? ""),
    );

    // Build CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `telemetry_${device.name || "device"}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const initialLoadRef = useRef(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refresherKey, setRefresherKey] = useState(0);


  const resolveNewestTelemetryTimestamp = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    return rows.reduce((newest, row) => {
      if (!row?.timestamp) return newest;
      if (!newest) return row.timestamp;

      return new Date(row.timestamp).getTime() > new Date(newest).getTime()
        ? row.timestamp
        : newest;
    }, null);
  };

  const compareTelemetryRows = (a, b) => {
    const aDeviceTime = new Date(a?.timestamp || 0).getTime();
    const bDeviceTime = new Date(b?.timestamp || 0).getTime();
    if (aDeviceTime !== bDeviceTime) {
      return bDeviceTime - aDeviceTime;
    }

    const aServerTime = new Date(a?.server_time || 0).getTime();
    const bServerTime = new Date(b?.server_time || 0).getTime();
    if (aServerTime !== bServerTime) {
      return bServerTime - aServerTime;
    }

    return String(b?.id || "").localeCompare(String(a?.id || ""));
  };

  const mergeTelemetryRows = (incomingRows, { prepend = true } = {}) => {
    if (!Array.isArray(incomingRows) || incomingRows.length === 0) {
      return [];
    }

    const uniqueRows = incomingRows.filter((row, index) => {
      const key = row.id || buildTelemetryRowId(row, index);
      if (telemetryKeysRef.current.has(key)) return false;
      telemetryKeysRef.current.add(key);
      return true;
    });

    if (uniqueRows.length === 0) {
      return [];
    }

    setLatestData((prev) =>
      [...(prepend ? uniqueRows : []), ...prev, ...(prepend ? [] : uniqueRows)]
        .sort(compareTelemetryRows)
        .slice(0, MAX_TELEMETRY_ROWS_IN_MEMORY),
    );

    return uniqueRows;
  };

  useEffect(() => {
    if (!device?.id) return;

    let isMounted = true;

    const fetchTelemetry = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const isInitialLoad = initialLoadRef.current;
        const params = {
          limit: isInitialLoad
            ? INITIAL_TELEMETRY_LIMIT
            : DELTA_TELEMETRY_LIMIT,
          include_total: "false",
          ...(lastTimestampRef.current && {
            since: lastTimestampRef.current,
          }),
        };

        // Initial full loader
        if (isInitialLoad) {
          setLoading(true);
        } else {
          // Background loader
          setRefreshing(true);
        }

        const res = await axios.get(
          `${API_BASE}/api/v1/devices/${device.id}/telemetry`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(activeOrg?.id && {
                "X-Org-ID": activeOrg.id,
              }),
            },
            params,
          },
        );

        if (!isMounted) return;

        const telemetryRowsFromApi = Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        const newRows = telemetryRowsFromApi.map((row, index) =>
          normalizeTelemetryRow(row, index),
        );

        if (isInitialLoad) {
          setTelemetryHasMore(Boolean(res.data?.has_more));
        }

        if (newRows.length > 0) {
          const newestTimestamp = resolveNewestTelemetryTimestamp(newRows);
          if (newestTimestamp) {
            lastTimestampRef.current = newestTimestamp;
          }

          mergeTelemetryRows(newRows, { prepend: true });
        }
      } catch (err) {
        console.error("Delta telemetry fetch failed", err);
      } finally {
        if (!isMounted) return;

        setLoading(false);
        setRefreshing(false);

        initialLoadRef.current = false;
      }
    };

    initialLoadRef.current = true;
    lastTimestampRef.current = null;
    telemetryKeysRef.current = new Set();
    setLatestData([]);
    setTelemetryPagination((prev) => ({ ...prev, page: 0 }));
    setTelemetryTotalCount(null);
    setTelemetryHasMore(false);
    setOlderTelemetryLoading(false);

    fetchTelemetry();

    const intervalId = setInterval(fetchTelemetry, 7000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [device?.id, activeOrg?.id, refresherKey]);

  useEffect(() => {
    if (!device?.id) return;

    let isMounted = true;

    const fetchTelemetryCount = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const headers = {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        };

        setTelemetryCountLoading(true);
        setTelemetryCountError("");

        let res;
        try {
          res = await axios.get(
            `${API_BASE}/api/v1/devices/${device.id}/telemetry/count`,
            { headers },
          );
        } catch (error) {
          if (error?.response?.status !== 404) {
            throw error;
          }

          // Backward-compatible fallback for deployments where the new count
          // endpoint is not live yet. It fetches one row plus the exact count.
          res = await axios.get(
            `${API_BASE}/api/v1/devices/${device.id}/telemetry`,
            {
              headers,
              params: {
                limit: 1,
                include_total: "true",
              },
            },
          );
        }

        if (!isMounted) return;

        const totalCount = Number(res.data?.total_count ?? res.data?.count);
        setTelemetryTotalCount(Number.isFinite(totalCount) ? totalCount : null);
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to fetch telemetry count", error);
        setTelemetryCountError("Telemetry total is still loading");
      } finally {
        if (isMounted) {
          setTelemetryCountLoading(false);
        }
      }
    };

    setTelemetryTotalCount(null);
    setTelemetryCountError("");
    fetchTelemetryCount();

    const intervalId = setInterval(fetchTelemetryCount, 60000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [device?.id, activeOrg?.id, refresherKey]);

  const handleLoadOlderTelemetry = async () => {
    if (!device?.id || olderTelemetryLoading || latestData.length === 0) {
      return;
    }

    const oldestRow = latestData[latestData.length - 1];
    if (!oldestRow?.timestamp) return;

    try {
      setOlderTelemetryLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const res = await axios.get(
        `${API_BASE}/api/v1/devices/${device.id}/telemetry`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
          params: {
            limit: INITIAL_TELEMETRY_LIMIT,
            include_total: "false",
            before: oldestRow.timestamp,
            ...(oldestRow.id && { before_id: oldestRow.id }),
          },
        },
      );

      const olderRows = (res.data?.data || []).map((row, index) =>
        normalizeTelemetryRow(row, index),
      );

      mergeTelemetryRows(olderRows, { prepend: false });
      setTelemetryHasMore(Boolean(res.data?.has_more));
    } catch (error) {
      console.error("Failed to load older telemetry", error);
    } finally {
      setOlderTelemetryLoading(false);
    }
  };

  useEffect(() => {
    if (!device?.id || !can(permissions, "firmware.view")) {
      setFirmwareHistory([]);
      return;
    }

    let mounted = true;

    const fetchFirmwareHistory = async () => {
      try {
        setFirmwareLoading(true);
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const res = await axios.get(
          `${API_BASE}/api/v1/devices/${device.id}/firmware/history`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
            },
          },
        );

        if (!mounted) return;

        const rows = Array.isArray(res?.data?.history)
          ? res.data.history
          : Array.isArray(res?.data)
            ? res.data
            : [];

        const sortedRows = [...rows].sort((a, b) => {
          const aTime = new Date(a?.created_at || a?.updated_at || 0).getTime();
          const bTime = new Date(b?.created_at || b?.updated_at || 0).getTime();
          return bTime - aTime;
        });

        setFirmwareHistory(sortedRows);
      } catch (error) {
        console.error("Failed to fetch firmware history", error);
        if (mounted) setFirmwareHistory([]);
      } finally {
        if (mounted) setFirmwareLoading(false);
      }
    };

    fetchFirmwareHistory();
    return () => {
      mounted = false;
    };
  }, [device?.id, activeOrg?.id, permissions, refresherKey]);

  useEffect(() => {
    if (!device?.id) {
      setLicenseHistory([]);
      return;
    }

    let mounted = true;

    const fetchLicenseHistory = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const res = await axios.get(
          `${API_BASE}/api/v1/licensing/devices/${device.id}/history`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
            },
          },
        );

        if (!mounted) return;
        setLicenseHistory(
          Array.isArray(res?.data?.history) ? res.data.history : [],
        );
      } catch (error) {
        console.error("Failed to fetch device license history", error);
        if (mounted) setLicenseHistory([]);
      }
    };

    fetchLicenseHistory();
    return () => {
      mounted = false;
    };
  }, [device?.id, activeOrg?.id, refresherKey]);

  const openLicenseAction = (actionType) => {
    setLicenseActionType(actionType);
    setLicenseActionError("");
    setLicenseActionSuccess("");
    setLicenseActionForm({
      durationValue:
        actionType === "suspend" || actionType === "revoke" ? "" : 1,
      durationUnit: "year",
      reason: "",
      releaseEntitlement: false,
    });
    setLicenseActionOpen(true);
  };

  const handleLicenseActionSubmit = async () => {
    if (!device?.id) return;

    const endpointMap = {
      activate: "activate",
      extend: "extend",
      suspend: "suspend",
      reactivate: "reactivate",
      revoke: "revoke",
    };

    const payload = {};
    if (["activate", "extend", "reactivate"].includes(licenseActionType)) {
      payload.duration_value = Number(licenseActionForm.durationValue);
      payload.duration_unit = licenseActionForm.durationUnit;
    }
    if (
      ["suspend", "revoke"].includes(licenseActionType) &&
      licenseActionForm.reason
    ) {
      payload.reason = licenseActionForm.reason;
    }
    if (licenseActionType === "revoke") {
      payload.release_entitlement = Boolean(
        licenseActionForm.releaseEntitlement,
      );
    }

    try {
      setLicenseActionLoading(true);
      setLicenseActionError("");
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_BASE}/api/v1/licensing/devices/${device.id}/${endpointMap[licenseActionType]}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      );
      setLicenseActionSuccess(`License ${licenseActionType} completed`);
      setLicenseActionOpen(false);
      await Promise.all([
        fetchDeviceDetails(),
        (async () => {
          const token = localStorage.getItem("authToken");
          const res = await axios.get(
            `${API_BASE}/api/v1/licensing/devices/${device.id}/history`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
              },
            },
          );
          setLicenseHistory(
            Array.isArray(res?.data?.history) ? res.data.history : [],
          );
        })(),
      ]);
    } catch (error) {
      console.error("Failed to update device license", error);
      setLicenseActionError(
        error?.response?.data?.error || "Failed to update device license",
      );
    } finally {
      setLicenseActionLoading(false);
    }
  };

  // ---------- Data type chip colors ----------
  const getTypeColor = (type) => {
    switch (type) {
      case "Boolean":
        return { bg: "#ede9fe", color: "#6d28d9" };
      case "Float":
        return { bg: "#e0f2fe", color: "#0369a1" };
      case "Integer":
        return { bg: "#fff7ed", color: "#c2410c" };
      default:
        return { bg: "#f3f4f6", color: "#374151" };
    }
  };

  const schemaRows = schema.map((row, index) => ({
    id: index + 1,
    sno: index + 1,
    key: row.key,
    type: normalizeFieldType(row.type === "Custom" ? row.customType : row.type),
    unit: resolveUnit(row),
  }));

  const schemaColumns = [
    {
      field: "sno",
      headerName: "S.No.",
      width: 80,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "key",
      headerName: "Key Name",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "100%", // ? fix
            width: "100%",
          }}
        >
          <Typography fontWeight={500}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Data Type",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const style = getTypeColor(params.value);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%", // ? fix
              width: "100%",
            }}
          >
            <Chip
              label={params.value}
              size="small"
              sx={{
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 600,
                borderRadius: "6px",
                height: 24,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "unit",
      headerName: "Unit",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%", // ? fix
            width: "100%",
          }}
        >
          <Typography color="text.secondary">{params.value || "-"}</Typography>
        </Box>
      ),
    },
  ];

  const telemetryRows = latestData.map((row, index) => ({
    ...row,
    id: row?.id || buildTelemetryRowId(row, index),
  }));

  const packetTypeOptions = useMemo(() => {
    return [
      "all",
      ...new Set(telemetryRows.map((row) => row.packet_type).filter(Boolean)),
    ];
  }, [telemetryRows]);

  const filteredTelemetryRows = useMemo(() => {
    if (packetTypeFilter === "all") {
      return telemetryRows;
    }

    return telemetryRows.filter((row) => row.packet_type === packetTypeFilter);
  }, [telemetryRows, packetTypeFilter]);

  const schemaByKey = useMemo(() => {
    const map = new Map();
    schema.forEach((item) => {
      if (item?.key) map.set(item.key, item);
    });
    return map;
  }, [schema]);

  const telemetryMetricKeys = useMemo(() => {
    const reservedKeys = new Set([
      "id",
      "timestamp",
      "server_time",
      "packet_type",
      "__raw_payload",
    ]);
    const keys = [];
    const seen = new Set();

    schema.forEach((item) => {
      if (!item?.key || seen.has(item.key)) return;
      seen.add(item.key);
      keys.push(item.key);
    });

    latestData.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (reservedKeys.has(key) || seen.has(key)) return;
        seen.add(key);
        keys.push(key);
      });
    });

    return keys;
  }, [latestData, schema]);

  // IST offset: 5 hours 30 minutes
  const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

  const formatLastSeen = (ts, enabled) => {
    if (!enabled) return "Disabled";
    if (!ts) return "Never";

    const now = Date.now();

    // Add 5h 30m to last_seen
    const lastSeenAdjusted = new Date(ts).getTime() + IST_OFFSET_MS;

    const diffMs = Math.max(0, now - lastSeenAdjusted);
    const mins = Math.floor(diffMs / 60000);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;

    const hrs = Math.floor(mins / 60);
    return `${hrs} hr ago`;
  };

  const telemetryColumns = [
    {
      field: "timestamp",
      headerName: "Device Time",
      minWidth: 250,
      flex: 1,
      renderCell: (params) => {
        let formattedDate = "-";

        if (params.value) {
          const date = new Date(params.value);

          // Subtract 5 hours 30 minutes (19800000 ms)
          date.setMinutes(date.getMinutes() - 330);

          formattedDate = date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          });
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography>{formattedDate}</Typography>
          </Box>
        );
      },
    },
    {
      field: "server_time",
      headerName: "Server Time",
      minWidth: 250,
      flex: 1,
      renderCell: (params) => {
        const formattedDate = params.value
          ? new Date(params.value).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })
          : "-";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography>{formattedDate}</Typography>
          </Box>
        );
      },
    },
    {
      field: "packet_type",
      headerName: "Packet Type",
      minWidth: 150,
      flex: 0.7,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const tone = packetTypeTone(params.value);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Chip
              label={String(params.value || "telemetry").replaceAll("_", " ")}
              size="small"
              sx={{
                textTransform: "capitalize",
                fontWeight: 700,
                backgroundColor: tone.bg,
                color: tone.color,
                boxSizing: "border-box", // ? prevent hover shift
              }}
            />
          </Box>
        );
      },
    },
    ...telemetryMetricKeys.map((key) => {
      const item = schemaByKey.get(key) || { key };
      return {
        field: key,
        headerName: resolveUnit(item) ? `${key} (${resolveUnit(item)})` : key,
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatTelemetryCellValue(params.value)}
            </Typography>
          </Box>
        ),
      };
    }),
  ];

  // const latestData = device?.latestData || [];

  // Loading state

  const allTabs = [
    { key: "overview", label: "Overview", perm: "device.view" },
    { key: "telemetry", label: "Telemetry", perm: "telemetry.view" },
    { key: "analytics", label: "Analytics", perm: "analytics.view" },
    { key: "alarms", label: "Alarms", perm: "alarm.view" },
    { key: "settings", label: "Settings", perm: "device.update" },
    { key: "commands", label: "Commands", perm: "device.control" },
    { key: "logs", label: "Logs", perm: "device_logs.view" },
    { key: "firmware", label: "Firmware", perm: "firmware.view" },
  ];

  // Filter based on permission
  const visibleTabs = allTabs.filter((tab) => can(permissions, tab.perm));
  const canFirmwareDiagnostics = can(permissions, "system.manage");
  const canFirmwareStatusUpdate = can(permissions, "device.update");

  const latestPoint = latestData.length ? latestData[0] : null;

  const latestRawPayload = useMemo(() => {
    if (!latestPoint) return null;

    const payloadData = getTelemetryPayload(latestPoint);
    return {
      timestamp: latestPoint?.timestamp || null,
      server_time: latestPoint?.server_time || null,
      packet_type: latestPoint?.packet_type || "telemetry",
      data: payloadData,
    };
  }, [latestPoint]);

  const latestRawPayloadText = useMemo(() => {
    if (!latestRawPayload) return "";
    try {
      return JSON.stringify(latestRawPayload, null, 2);
    } catch (error) {
      return String(latestRawPayload);
    }
  }, [latestRawPayload]);

  const handleOpenRawJsonDialog = () => {
    setRawJsonCopyStatus("");
    setRawJsonDialogOpen(true);
  };

  const handleCloseRawJsonDialog = () => {
    setRawJsonDialogOpen(false);
    setRawJsonCopyStatus("");
  };

  const handleCopyRawJson = async () => {
    if (!latestRawPayloadText) return;
    try {
      await navigator.clipboard.writeText(latestRawPayloadText);
      setRawJsonCopyStatus("Raw JSON copied");
    } catch (error) {
      setRawJsonCopyStatus("Copy failed. Please copy manually.");
    }
  };
  const typeConfig = deviceTypeDetails?.config || {
    dashboard_config: [],
    threshold_rules: [],
    alert_templates: [],
    business_rules: [],
  };

  const widgetSeries = useMemo(() => {
    const points = latestData.slice(0, 30).reverse();
    const mapped = {};

    points.forEach((row) => {
      const label = row.server_time || row.timestamp || "--";
      schema.forEach((field) => {
        if (!field?.key) return;
        if (!mapped[field.key]) mapped[field.key] = [];
        mapped[field.key].push({
          time: String(label).slice(11, 19),
          value: Number(row[field.key]),
        });
      });
    });

    return mapped;
  }, [latestData, schema]);

  const dashboardWidgets = useMemo(() => {
    if (!Array.isArray(typeConfig.dashboard_config) || !latestPoint) return [];

    return typeConfig.dashboard_config
      .filter((widget) => widget?.metric_key)
      .map((widget, index) => {
        const field =
          schema.find((item) => item.key === widget.metric_key) || {};
        const value = latestPoint?.[widget.metric_key];
        const unit = resolveUnit(field);
        const status = evaluateThreshold(
          typeConfig.threshold_rules,
          widget.metric_key,
          value,
        );

        return {
          id: `${widget.metric_key}-${index}`,
          title: widget.title || field.label || widget.metric_key,
          metricKey: widget.metric_key,
          widgetType: widget.widget_type || "stat",
          size: widget.size || "md",
          unit,
          numericValue: Number(value),
          value: toDisplayValue(value, unit),
          status,
          color: getWidgetColor(index),
          chartData: widgetSeries[widget.metric_key] || [],
        };
      });
  }, [
    typeConfig.dashboard_config,
    typeConfig.threshold_rules,
    latestPoint,
    schema,
    widgetSeries,
  ]);

  const firmwareStats = useMemo(() => {
    if (!Array.isArray(firmwareHistory) || firmwareHistory.length === 0) {
      return { total_updates: 0, successful: 0, failed: 0 };
    }

    const successStates = new Set(["INSTALLED", "COMPLETED", "SUCCESS"]);
    const failureStates = new Set(["FAILED", "ERROR", "ROLLED_BACK"]);

    let successful = 0;
    let failed = 0;

    firmwareHistory.forEach((item) => {
      const status = String(item?.status || "").toUpperCase();
      if (successStates.has(status)) successful += 1;
      if (failureStates.has(status)) failed += 1;
    });

    return {
      total_updates: firmwareHistory.length,
      successful,
      failed,
    };
  }, [firmwareHistory]);

  const firmwareOverview = useMemo(() => {
    const latest = firmwareHistory[0] || {};

    const currentVersion =
      device?.firmware_version ||
      device?.current_firmware_version ||
      device?.firmware?.version ||
      latest?.to_version ||
      latest?.firmware_version ||
      "--";

    const latestStatus = String(latest?.status || "").toUpperCase();
    const isUpToDate =
      latestStatus === "INSTALLED" ||
      latestStatus === "COMPLETED" ||
      latestStatus === "SUCCESS";

    return {
      version: currentVersion,
      status:
        firmwareHistory.length === 0
          ? "unknown"
          : isUpToDate
            ? "up_to_date"
            : "attention",
      last_updated_at: latest?.created_at || latest?.updated_at || null,
      update_method: latest?.rollout_strategy || "OTA",
    };
  }, [firmwareHistory, device]);

  const typeHighlights = [
    {
      label: "Widgets",
      value: typeConfig.dashboard_config?.length || 0,
      icon: <DashboardCustomizeRoundedIcon fontSize="small" />,
    },
    {
      label: "Thresholds",
      value: typeConfig.threshold_rules?.length || 0,
      icon: <WarningAmberRoundedIcon fontSize="small" />,
    },
    {
      label: "Alerts",
      value: typeConfig.alert_templates?.length || 0,
      icon: <NotificationsActiveRoundedIcon fontSize="small" />,
    },
    {
      label: "Business rules",
      value: typeConfig.business_rules?.length || 0,
      icon: <RuleRoundedIcon fontSize="small" />,
    },
  ];

  const telemetryCountLabel =
    typeof telemetryTotalCount === "number"
      ? formatTelemetryCount(telemetryTotalCount)
      : telemetryCountLoading
        ? "Counting..."
        : telemetryCountError
          ? "Retrying..."
          : "-";

  const heroStats = [
    {
      label: "Device ID",
      value: `${(device?.id || deviceId || "").slice(0, 8)}...`,
      icon: <MemoryRoundedIcon fontSize="small" />,
    },
    {
      label: "Schema fields",
      value: schema.length,
      icon: <SensorsRoundedIcon fontSize="small" />,
    },
    {
      label: "Telemetry points",
      value: telemetryCountLabel,
      icon: <QueryStatsRoundedIcon fontSize="small" />,
    },
    {
      label: "Connection state",
      value: deviceStatus?.label || "Unknown",
      icon: <BoltRoundedIcon fontSize="small" />,
    },
  ];

  useEffect(() => {
    if (activeTab >= visibleTabs.length) {
      setActiveTab(0);
    }
  }, [visibleTabs.length, activeTab]);

  if (!device) {
    return (
      <Box p={4}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {deviceMetaLoading ? <CircularProgress size={22} /> : null}
          <Typography>
            {deviceMetaLoading
              ? "Loading device details..."
              : "Device details unavailable"}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 3,
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 3 } }}>
        <Card
          sx={{
            mb: 3,
            overflow: "hidden",
            borderRadius: 6,
            border: "1px solid rgba(148, 163, 184, 0.18)",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            boxShadow: "0 32px 80px rgba(15, 23, 42, 0.18)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Breadcrumbs
              sx={{ mb: 2, color: "rgba(226,232,240,0.72)" }}
              separator="›"
            >
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate(-1)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#E0F2FE",
                  fontWeight: 600,
                }}
              >
                <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                Devices
              </Link>

              <Typography sx={{ color: "rgba(226,232,240,0.72)" }}>
                Device Details
              </Typography>
            </Breadcrumbs>

            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    sx={{
                      color: "#F8FAFC",
                      fontSize: { xs: 28, md: 40 },
                      fontWeight: 600,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {device?.name}
                  </Typography>
                  {deviceStatus && (
                    <Chip
                      label={deviceStatus.label}
                      sx={{
                        color: "#E2FBE8",
                        backgroundColor: "rgba(34,197,94,0.14)",
                        border: "1px solid rgba(134,239,172,0.18)",
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
                <Typography
                  sx={{
                    mt: 1,
                    maxWidth: 680,
                    color: "rgba(226,232,240,0.80)",
                    lineHeight: 1.7,
                  }}
                >
                  Operational view for telemetry, diagnostics, alarms, settings,
                  firmware, and audit activity for this connected asset.
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setRefresherKey((prev) => prev + 1)}
                sx={{
                  borderRadius: 999,
                  color: "#E2E8F0",
                  borderColor: "rgba(226,232,240,0.18)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Refresh
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                onClick={handleDownloadCSV}
                sx={{
                  borderRadius: 999,
                  color: "#E2E8F0",
                  borderColor: "rgba(226,232,240,0.18)",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Export Telemetry
              </Button>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              sx={{ mt: 3 }}
            >
              {heroStats.map((stat) => (
                <Card
                  key={stat.label}
                  sx={{
                    flex: 1,
                    borderRadius: 4,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    boxShadow: "none",
                    color: "#F8FAFC",
                  }}
                >
                  <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography
                          sx={{ fontSize: 12, color: "rgba(226,232,240,0.72)" }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          sx={{ mt: 0.5, fontSize: 22, fontWeight: 700 }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: "rgba(255,255,255,0.82)",
                          color: "#0F4C81",
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            ...panelSx,
            px: { xs: 1, md: 2 },
            pt: 1,
            pb: 0.5,
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                minWidth: "auto",
                px: { xs: 2, md: 2.5 },
                py: 1.5,
                mr: 1,
                borderRadius: 999,
                textTransform: "none",
                fontSize: 15,
                fontWeight: 700,
                color: "#64748B",
              },
              "& .Mui-selected": {
                color: "#0F4C81 !important",
                backgroundColor: "rgba(14,165,233,0.10)",
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 999,
                background: "linear-gradient(90deg, #0EA5E9, #14B8A6)",
              },
            }}
          >
            {visibleTabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box>
          {/* ================= OVERVIEW ================= */}
          {visibleTabs[activeTab]?.key === "overview" && (
            <>
              <Card sx={{ ...panelSx, mb: 3 }}>
                <CardHeader
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2 },
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: 16, sm: 20, md: 20 },
                        lineHeight: 1.2,
                        color: "#fff",
                      }}
                    >
                      Device Information
                    </Typography>
                  }
                  subheader={
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: { xs: 13, sm: 14 },
                        mt: 0.5,
                        lineHeight: 1.5,
                        color: "#d3d3d3",
                      }}
                    >
                      Identity, ownership, and connection health for this
                      device.
                    </Typography>
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Info label="Device ID" value={device.id || deviceId} />
                    <Info label="Device Name" value={device.name || "-"} />
                    <Info label="Device Owner" value={device.owner_id || "-"} />
                    <Info
                      label="Last Seen At"
                      value={
                        formatLastSeen(device.last_seen, device.is_active) ||
                        "-"
                      }
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ ...panelSx, mb: 3 }}>
                <CardHeader
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2 },
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: 16, sm: 20, md: 20 },
                        lineHeight: 1.2,
                        color: "#fff",
                      }}
                    >
                      Device Licensing
                    </Typography>
                  }
                  subheader={
                    <Typography
                      sx={{
                        color: "#d3d3d3",
                        fontSize: { xs: 13, sm: 14 },
                        mt: 0.5,
                        lineHeight: 1.5,
                      }}
                    >
                      Runtime entitlement state, expiry, and commercial
                      lifecycle controls.
                    </Typography>
                  }
                  action={
                    licenseStatus ? (
                      <Chip
                        label={licenseStatus.label}
                        sx={{
                          mt: 1.5,
                          mr: 1,
                          fontWeight: 700,
                          color: "#fff",
                          backgroundColor: licenseStatus.color,
                        }}
                      />
                    ) : null
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  {licenseActionSuccess ? (
                    <Alert
                      severity="success"
                      sx={{ mb: 2 }}
                      onClose={() => setLicenseActionSuccess("")}
                    >
                      {licenseActionSuccess}
                    </Alert>
                  ) : null}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Info
                      label="License State"
                      value={licenseStatus?.label || "-"}
                    />
                    <Info
                      label="License Source"
                      value={device?.license?.source || "-"}
                    />
                    <Info
                      label="Expires On"
                      value={
                        device?.license?.assignment?.license_expires_at || "-"
                      }
                    />
                    <Info
                      label="Days Remaining"
                      value={
                        device?.license?.days_remaining !== null &&
                        device?.license?.days_remaining !== undefined
                          ? device.license.days_remaining
                          : "-"
                      }
                    />
                  </Box>

                  {can(permissions, "organization.update") ? (
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 2.5 }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => openLicenseAction("activate")}
                      >
                        Activate
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => openLicenseAction("extend")}
                      >
                        Extend
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => openLicenseAction("suspend")}
                      >
                        Suspend
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => openLicenseAction("reactivate")}
                      >
                        Reactivate
                      </Button>
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={() => openLicenseAction("revoke")}
                      >
                        Revoke
                      </Button>
                    </Stack>
                  ) : null}

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{ fontWeight: 700, color: "#0F172A", mb: 1.5 }}
                    >
                      License History
                    </Typography>
                    {licenseHistory.length ? (
                      <Stack spacing={1.25}>
                        {licenseHistory.slice(0, 5).map((entry, index) => (
                          <Box
                            key={entry.id}
                            sx={{
                              p: 1.75,
                              borderRadius: 3,
                              border: "1px solid rgba(226, 232, 240, 0.95)",
                              backgroundColor: "#F8FAFC",
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", md: "row" }}
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Typography
                                sx={{ fontWeight: 700, color: "#0F172A" }}
                              >
                                #{index + 1}{" "}
                                {String(entry.status || "-").toUpperCase()}
                              </Typography>
                              <Typography
                                sx={{ color: "#64748B", fontSize: 13 }}
                              >
                                {entry.license_start_at ||
                                  entry.assigned_at ||
                                  "-"}
                              </Typography>
                            </Stack>
                            <Typography
                              sx={{ mt: 0.75, color: "#475569", fontSize: 14 }}
                            >
                              Source: {entry.activation_source || "-"} |
                              Expires: {entry.license_expires_at || "-"}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">
                        No runtime license history available yet.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {device?.commissioning ? (
                <Card sx={{ ...panelSx, mb: 3 }}>
                  <CardHeader
                    sx={{
                      px: { xs: 2, sm: 2.5 },
                      py: { xs: 1.5, sm: 2 },
                      background:
                        "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                    }}
                    title={
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: 18, sm: 20, md: 22 },
                          lineHeight: 1.2,
                          color: "#fff",
                        }}
                      >
                        Commissioning
                      </Typography>
                    }
                    subheader={
                      <Typography
                        sx={{
                          color: "#d3d3d3",
                          fontSize: { xs: 13, sm: 14 },
                          mt: 0.5,
                          lineHeight: 1.5,
                        }}
                      >
                        Project assignment, install capture, and field
                        verification for this device.
                      </Typography>
                    }
                  />
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <Info
                        label="Project"
                        value={device.commissioning?.project?.name || "-"}
                      />
                      <Info
                        label="Site"
                        value={device.commissioning?.site?.name || "-"}
                      />
                      <Info
                        label="Provisioning Mode"
                        value={device.commissioning?.provisioning_mode || "-"}
                      />
                      <Info
                        label="Commissioning Status"
                        value={device.commissioning?.status || "-"}
                      />
                      <Info
                        label="Commissioned At"
                        value={device.commissioning?.commissioned_at || "-"}
                      />
                      <Info
                        label="Installation Location"
                        value={
                          device.commissioning?.installation?.location_label ||
                          "-"
                        }
                      />
                      <Info
                        label="Latitude"
                        value={
                          device.commissioning?.installation?.latitude ?? "-"
                        }
                      />
                      <Info
                        label="Longitude"
                        value={
                          device.commissioning?.installation?.longitude ?? "-"
                        }
                      />
                      <Info
                        label="Config Pushed"
                        value={
                          device.commissioning?.verification?.config_pushed ===
                          true
                            ? "Yes"
                            : device.commissioning?.verification
                                  ?.config_pushed === false
                              ? "No"
                              : "-"
                        }
                      />
                      <Info
                        label="Device Acknowledged"
                        value={
                          device.commissioning?.verification
                            ?.device_acknowledged === true
                            ? "Yes"
                            : device.commissioning?.verification
                                  ?.device_acknowledged === false
                              ? "No"
                              : "-"
                        }
                      />
                    </Box>
                    {Array.isArray(device.commissioning?.evidence?.photos) &&
                    device.commissioning.evidence.photos.length ? (
                      <Box sx={{ mt: 2.5 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: "#64748B",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            mb: 1.2,
                          }}
                        >
                          Installation Photos
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 1.4,
                          }}
                        >
                          {device.commissioning.evidence.photos.map(
                            (photo, index) => (
                              <Paper
                                key={`${photo.file_url || "commissioning-photo"}-${index}`}
                                elevation={0}
                                sx={{
                                  p: 1.2,
                                  borderRadius: 3,
                                  border: "1px solid #E2E8F0",
                                  backgroundColor: "#FFF",
                                }}
                              >
                                <Typography
                                  sx={{
                                    mt: 1,
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: "#0F172A",
                                  }}
                                >
                                  {photo.label || `Photo ${index + 1}`}
                                </Typography>
                                {(
                                  photo.download_url || photo.file_url
                                )?.startsWith("http") ? (
                                  <Box
                                    component="img"
                                    src={photo.download_url || photo.file_url}
                                    alt={
                                      photo.label ||
                                      `Commissioning photo ${index + 1}`
                                    }
                                    sx={{
                                      width: "100%",
                                      height: 180,
                                      objectFit: "cover",
                                      borderRadius: 2.5,
                                      backgroundColor: "#E2E8F0",
                                    }}
                                  />
                                ) : null}

                                {/* <Typography
                                  sx={{
                                    mt: 0.4,
                                    fontSize: 12,
                                    color: "#64748B",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {photo.download_url || photo.file_url || "-"}
                                </Typography> */}
                              </Paper>
                            ),
                          )}
                        </Box>
                      </Box>
                    ) : null}
                  </CardContent>
                </Card>
              ) : null}

              <Card sx={{ ...panelSx, mb: 3 }}>
                <CardHeader
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.8, sm: 2.2 },
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 1.5, sm: 2 }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      sx={{ width: "100%" }}
                    >
                      {/* Left Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 16, sm: 20, md: 20 },
                            mb: 0.5,
                            lineHeight: 1.2,
                            color: "#fff",
                          }}
                        >
                          Device Type Intelligence
                        </Typography>

                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: 13, sm: 14 },
                            lineHeight: 1.5,
                            color: "#cdcaca",
                          }}
                        >
                          Schema, dashboard widgets, thresholds, and alert
                          defaults inherited from the assigned device type.
                        </Typography>
                      </Box>

                      {/* Right Chip */}
                      <Chip
                        label={
                          deviceTypeDetails?.name ||
                          device?.device_type?.name ||
                          "Unassigned type"
                        }
                        sx={{
                          mt: { xs: 1, sm: 0 },
                          borderRadius: 999,
                          color: "#dbdbdb",
                          backgroundColor: "rgba(13, 158, 220, 0.1)",
                          fontWeight: 700,
                          maxWidth: { xs: "100%", sm: 220 },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      />
                    </Stack>
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr 1fr",
                        xl: "repeat(4, 1fr)",
                      },
                      gap: 1.5,
                      mb: 3,
                    }}
                  >
                    {typeHighlights.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          p: 2,
                          borderRadius: 4,
                          border: "1px solid rgba(226, 232, 240, 0.95)",
                          background:
                            "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "#64748B",
                                fontWeight: 700,
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.5,
                                fontSize: 24,
                                fontWeight: 800,
                                color: "#0F172A",
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              backgroundColor: "#EFF6FF",
                              color: "#0F4C81",
                            }}
                          >
                            {item.icon}
                          </Avatar>
                        </Stack>
                      </Box>
                    ))}
                  </Box>

                  {dashboardWidgets.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(2, minmax(0, 1fr))",
                          xl: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {dashboardWidgets.map((widget) => (
                        <Box
                          key={widget.id}
                          sx={{
                            p: 2,
                            borderRadius: 4,
                            border: "1px solid rgba(226, 232, 240, 0.95)",
                            backgroundColor: "#fff",
                            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                            gridColumn: {
                              xs: "span 1",
                              md: widget.size === "lg" ? "span 1" : "span 1",
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "#64748B",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                }}
                              >
                                {widget.widgetType}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.75,
                                  fontSize: 18,
                                  fontWeight: 700,
                                  color: "#0F172A",
                                }}
                              >
                                {widget.title}
                              </Typography>
                            </Box>
                            <Chip
                              label={widget.status.label}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                color: widget.status.tone,
                                backgroundColor: widget.status.bg,
                              }}
                            />
                          </Stack>
                          <Typography
                            sx={{
                              mt: 2.5,
                              fontSize: 17,
                              fontWeight: 800,
                              color: "#111827",
                              letterSpacing: "-0.03em",
                            }}
                          >
                            {widget.value}
                          </Typography>
                          <Typography
                            sx={{ mt: 1.75, color: "#64748B", fontSize: 15 }}
                          >
                            Metric key: {widget.metricKey}
                          </Typography>
                          {widget.widgetType === "gauge" ? (
                            <Box sx={{ mt: 2 }}>
                              <LinearProgress
                                variant="determinate"
                                value={getGaugePercent(
                                  widget,
                                  widget.numericValue,
                                  typeConfig.threshold_rules,
                                )}
                                sx={{
                                  height: 10,
                                  borderRadius: 999,
                                  backgroundColor: "rgba(148, 163, 184, 0.18)",
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    backgroundColor: widget.color,
                                  },
                                }}
                              />
                            </Box>
                          ) : widget.widgetType !== "stat" ? (
                            <Box sx={{ mt: 2, height: 170 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <WidgetChart widget={widget} />
                              </ResponsiveContainer>
                            </Box>
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        border: "1px dashed rgba(148, 163, 184, 0.5)",
                        backgroundColor: "#F8FAFC",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
                        No dashboard widgets configured yet
                      </Typography>
                      <Typography sx={{ mt: 0.75, color: "#64748B" }}>
                        Open the Device Type Studio to define schema-driven KPI
                        cards, thresholds, and alert defaults for this device
                        family.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card sx={panelSx}>
                <CardHeader
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2 },
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: 16, sm: 20, md: 20 },
                        lineHeight: 1.2,
                        color: "#fff",
                      }}
                    >
                      Schema
                    </Typography>
                  }
                  subheader={
                    <Typography
                      sx={{
                        color: "#cac6c6",
                        fontSize: { xs: 13, sm: 14 },
                        mt: 0.5,
                        lineHeight: 1.5,
                      }}
                    >
                      Data keys, types, and engineering units configured for the
                      device.
                    </Typography>
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  {schema.length === 0 ? (
                    <Typography color="text.secondary">
                      No schema defined
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        height: 400,
                        width: "100%",
                        border: "1px solid rgba(226, 232, 240, 0.9)",
                        borderRadius: 4,
                        overflowX: "auto",
                        backgroundColor: "#fff",
                      }}
                    >
                      <DataGrid
                        rows={schemaRows}
                        columns={schemaColumns}
                        density="compact"
                        loading={deviceMetaLoading}
                        initialState={{
                          pagination: {
                            paginationModel: { page: 0, pageSize: 20 },
                          },
                        }}
                        pageSizeOptions={[15, 20, 25]}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        sx={gridSurfaceSx}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* TELEMETRY */}
          {visibleTabs[activeTab]?.key === "telemetry" &&
            can(permissions, "telemetry.view") && (
              <Card sx={{ ...panelSx, mb: 4 }}>
                <CardHeader
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.8, sm: 2.2 },
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={2}
                      mb={2}
                    >
                      <Box>
                        <Typography fontWeight={700} fontSize={22} color="#fff">
                          Latest Telemetry
                        </Typography>
                        <Typography color="#cfcece" sx={{ mt: 0.5 }}>
                          Live and recent payloads received from the device.
                          {typeof telemetryTotalCount === "number"
                            ? ` Showing ${telemetryRows.length} loaded rows out of ${formatTelemetryCount(telemetryTotalCount)} total points.`
                            : telemetryCountLoading
                              ? ` Showing ${telemetryRows.length} loaded rows. Counting full history...`
                              : ` Showing ${telemetryRows.length} loaded rows.`}
                          {telemetryHasMore
                            ? " Older history is available."
                            : " No older history is currently loaded."}
                          {refreshing ? "" : ""}
                        </Typography>
                      </Box>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", sm: "center" }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleOpenRawJsonDialog}
                          disabled={!latestPoint}
                          sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#dfdcdc",
                          }}
                        >
                          View as Raw JSON
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadRoundedIcon />}
                          onClick={handleDownloadCSV}
                          sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#dfdcdc",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            minWidth: "fit-content",
                            px: 2,
                          }}
                        >
                          Export CSV
                        </Button>
                      </Stack>
                    </Stack>
                  }
                />
                <Box
                  sx={{
                    px: { xs: 2.5, md: 3 },
                    pt: 0,
                    pb: 1.5,
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Stack spacing={0.4}>
                    <Typography fontWeight={700} color="#0f172a">
                      {telemetryHasMore
                        ? "Older telemetry is available"
                        : "Telemetry history loaded"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {telemetryHasMore
                        ? "Load the next historical batch to browse older rows."
                        : "No additional historical rows are currently cached."}
                    </Typography>
                  </Stack>

                  <TextField
                    select
                    size="small"
                    label="Packet Type"
                    value={packetTypeFilter}
                    onChange={(e) => setPacketTypeFilter(e.target.value)}
                    sx={{
                      minWidth: 200,
                      marginTop: "0.5rem",
                    }}
                  >
                    {packetTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === "all"
                          ? "All Packet Types"
                          : type.replaceAll("_", " ")}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="outlined"
                    onClick={handleLoadOlderTelemetry}
                    disabled={
                      !telemetryHasMore ||
                      olderTelemetryLoading ||
                      latestData.length === 0
                    }
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      minWidth: 200,
                    }}
                  >
                    {olderTelemetryLoading
                      ? "Loading older..."
                      : telemetryHasMore
                        ? "Load older telemetry"
                        : "No older telemetry"}
                  </Button>
                </Box>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box sx={{ height: 420, width: "100%" }}>
                    <DataGrid
                      rows={filteredTelemetryRows}
                      columns={telemetryColumns}
                      density="compact"
                      loading={loading}
                      paginationModel={telemetryPagination}
                      onPaginationModelChange={setTelemetryPagination}
                      pageSizeOptions={[15, 20, 25]}
                      disableColumnFilter
                      disableRowSelectionOnClick
                      disableColumnMenu
                      sx={gridSurfaceSx}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

          {/* ANALYTICS */}
          {visibleTabs[activeTab]?.key === "analytics" &&
            can(permissions, "stats.view") && (
              <Card sx={panelSx}>
                {/* <CardContent sx={{ p: { xs: 2.5, md: 3 } }}> */}
                <AnalyticsModel
                key={refresherKey}
                  device={{
                    ...device,
                    schema,
                  }}
                />
                {/* </CardContent> */}
              </Card>
            )}

          {/* ALARMS */}
          {visibleTabs[activeTab]?.key === "alarms" && (
            <Card sx={panelSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <DeviceAlarms 
                key={refresherKey}
                device={device} />
              </CardContent>
            </Card>
          )}

          {/* SETTINGS */}
          {visibleTabs[activeTab]?.key === "settings" &&
            can(permissions, "device.update") && (
              <Card sx={panelSx}>
                <CardHeader
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Typography
                      fontWeight={700}
                      fontSize={22}
                      mb={0.5}
                      color="#fff"
                    >
                      Device Settings
                    </Typography>
                  }
                  subheader={
                    <Typography color="#c8c8c8" mb={0}>
                      Manage credentials, remote access, and connection tooling.
                    </Typography>
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 }, pt: 0 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs
                      value={settingTab}
                      onChange={(e, newValue) => setSettingTab(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        "& .MuiTab-root": {
                          minWidth: "auto",
                          textTransform: "none",
                          fontWeight: 700,
                          color: "#64748B",
                        },
                        "& .Mui-selected": {
                          color: "#0F4C81 !important",
                        },
                      }}
                    >
                      <Tab label="Setting Credentials" />
                      {/* <Tab label="Remote Access" /> */}
                      <Tab label="Connection Types" />
                    </Tabs>
                  </Box>

                  {settingTab === 0 && <DeviceSettings 
                  key={refresherKey}
                  deviceId={device.id} />}
                  {/* {settingTab === 1 && <RemoteAccess />} */}
                  {settingTab === 1 && <CurlMqttCard deviceId={deviceId} />}
                </CardContent>
              </Card>
            )}

          {visibleTabs[activeTab]?.key === "commands" &&
            can(permissions, "device.control") && (
              <Card sx={panelSx}>
                <CardHeader
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Typography
                      fontWeight={700}
                      fontSize={22}
                      mb={0.5}
                      color="#fff"
                    >
                      Device Commands
                    </Typography>
                  }
                  subheader={
                    <Typography color="#c8c8c8" mb={0}>
                      Trigger remote actions, inspect live acknowledgements, and
                      review recent device command activity.
                    </Typography>
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <DeviceCommandsCard 
                  key={refresherKey}
                  deviceId={device.id} />
                </CardContent>
              </Card>
            )}

          {/* LOGS */}
          {visibleTabs[activeTab]?.key === "logs" && (
            <>
              {can(permissions, "audit.view") && (
                <Card sx={{ ...panelSx, mb: 3 }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <AuditLogsTable
                    key={`audit-${refresherKey}`}
                      entityType="device"
                      entityId={device.id}
                      height={260}
                    />
                  </CardContent>
                </Card>
              )}

              {can(permissions, "device_logs.view") && (
                <Card sx={panelSx}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <DeviceLogsTable deviceId={device.id} />
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* FIRMWARE */}
          {visibleTabs[activeTab]?.key === "firmware" &&
            can(permissions, "firmware.view") && (
              <Card sx={panelSx}>
                <CardHeader
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  }}
                  title={
                    <Typography
                      fontWeight={700}
                      fontSize={22}
                      mb={0.5}
                      color="#fff"
                    >
                      Firmware Lifecycle
                    </Typography>
                  }
                  subheader={
                    <Typography color="#c6c6c6" mb={0}>
                      Version posture, deployment history, and operational
                      update status.
                    </Typography>
                  }
                />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <FirmwareOverview
                  key={`overview-${refresherKey}`}
                    overview={firmwareOverview}
                    loading={firmwareLoading}
                  />
                  <FirmwareStats
                  key={`stats-${refresherKey}`}
                    stats={firmwareStats}
                    loading={firmwareLoading}
                  />
                  <FirmwareUpdateHistory
                   key={`history-${refresherKey}`}
                    rows={firmwareHistory}
                    loading={firmwareLoading}
                  />
                  <FirmwareDiagnosticsPanel
                    deviceId={device?.id}
                    activeOrgId={activeOrg?.id}
                    canRunChecks={canFirmwareDiagnostics}
                    canSubmitStatus={
                      canFirmwareDiagnostics && canFirmwareStatusUpdate
                    }
                  />
                </CardContent>
              </Card>
            )}
        </Box>
      </Box>

      <Dialog
        open={rawJsonDialogOpen}
        onClose={handleCloseRawJsonDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Latest Telemetry Raw JSON
        </DialogTitle>
        <DialogContent dividers>
          {latestRawPayloadText ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#0F172A",
                color: "#E2E8F0",
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: "auto",
                maxHeight: 460,
              }}
            >
              {latestRawPayloadText}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No latest telemetry value is available yet.
            </Typography>
          )}

          {rawJsonCopyStatus ? (
            <Typography
              variant="body2"
              sx={{
                mt: 1.25,
                color: rawJsonCopyStatus.includes("failed")
                  ? "#B91C1C"
                  : "#166534",
              }}
            >
              {rawJsonCopyStatus}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseRawJsonDialog}>Close</Button>
          <Button
            variant="contained"
            onClick={handleCopyRawJson}
            disabled={!latestRawPayloadText}
          >
            Copy JSON
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={licenseActionOpen}
        onClose={() => {
          if (!licenseActionLoading) setLicenseActionOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {licenseActionType.charAt(0).toUpperCase() +
            licenseActionType.slice(1)}{" "}
          Device License
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {licenseActionError ? (
              <Alert severity="error">{licenseActionError}</Alert>
            ) : null}
            {["activate", "extend", "reactivate"].includes(
              licenseActionType,
            ) ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Duration Value"
                  type="number"
                  fullWidth
                  value={licenseActionForm.durationValue}
                  onChange={(e) =>
                    setLicenseActionForm((prev) => ({
                      ...prev,
                      durationValue: e.target.value,
                    }))
                  }
                />
                <TextField
                  select
                  label="Duration Unit"
                  fullWidth
                  value={licenseActionForm.durationUnit}
                  onChange={(e) =>
                    setLicenseActionForm((prev) => ({
                      ...prev,
                      durationUnit: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                </TextField>
              </Stack>
            ) : null}

            {["suspend", "revoke"].includes(licenseActionType) ? (
              <TextField
                label="Reason"
                fullWidth
                multiline
                minRows={3}
                value={licenseActionForm.reason}
                onChange={(e) =>
                  setLicenseActionForm((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
              />
            ) : null}

            {licenseActionType === "revoke" ? (
              <TextField
                select
                label="Release Entitlement"
                fullWidth
                value={licenseActionForm.releaseEntitlement ? "yes" : "no"}
                onChange={(e) =>
                  setLicenseActionForm((prev) => ({
                    ...prev,
                    releaseEntitlement: e.target.value === "yes",
                  }))
                }
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setLicenseActionOpen(false)}
            disabled={licenseActionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLicenseActionSubmit}
            disabled={licenseActionLoading}
          >
            {licenseActionLoading ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Info({ label, value }) {
  const isDeviceIdLabel =
    typeof label === "string" && label.trim().toLowerCase() === "device id";
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 4,
        border: "1px solid rgba(226, 232, 240, 0.95)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: "#64748B",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: "#0F172A",
          fontWeight: 700,
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          whiteSpace: isDeviceIdLabel ? "normal" : "inherit",
          userSelect: isDeviceIdLabel ? "text" : "auto",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function WidgetChart({ widget }) {
  const data = Array.isArray(widget.chartData) ? widget.chartData : [];
  const sharedProps = {
    data,
    margin: { top: 8, right: 8, left: -20, bottom: 0 },
  };

  if (!data.length) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          // justifyContent: "center",
          width: "100%",
          color: "#94A3B8",
          fontSize: 13,
          whiteSpace: "nowrap",
          margin: 5,
        }}
      >
        No chart data yet
      </Box>
    );
  }

  const commonAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
      <XAxis
        dataKey="time"
        tick={{ fontSize: 11, fill: "#64748B" }}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        tick={{ fontSize: 11, fill: "#64748B" }}
        tickLine={false}
        axisLine={false}
        width={36}
      />
      <Tooltip />
    </>
  );

  if (widget.widgetType === "area") {
    return (
      <AreaChart {...sharedProps}>
        {commonAxes}
        <Area
          type="monotone"
          dataKey="value"
          stroke={widget.color}
          fill={widget.color}
          fillOpacity={0.18}
          strokeWidth={2}
        />
      </AreaChart>
    );
  }

  if (widget.widgetType === "bar") {
    return (
      <BarChart {...sharedProps}>
        {commonAxes}
        <Bar dataKey="value" fill={widget.color} radius={[6, 6, 0, 0]} />
      </BarChart>
    );
  }

  return (
    <LineChart {...sharedProps}>
      {commonAxes}
      <Line
        type="monotone"
        dataKey="value"
        stroke={widget.color}
        strokeWidth={2.5}
        dot={false}
      />
    </LineChart>
  );
}
